import React, { useState, useEffect } from 'react';
import { 
    Row, 
    Col, 
    Card, 
    Button, 
    Spinner, 
    Badge, 
    Modal, 
    Form,
    Alert,
    ProgressBar,
    Table,
    Dropdown
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faBullseye,
    faPlus,
    faEdit,
    faTrash,
    faPlay,
    faPause,
    faStop,
    faEye,
    faCog,
    faSync,
    faExclamationTriangle,
    faUserTie,
    faBoxOpen,
    faCalendarAlt,
    faAward,
    faFileAlt,
    faClock,
    faUsers,
    faChartLine,
    faInfoCircle,
    faSave,
    faCheck,
    faPercent,
    faDollarSign,
    faHandshake
} from '@fortawesome/free-solid-svg-icons';
import { 
    useSellerbotCampaigns,
    useSellerbotProfiles,
    useSellerbotProducts,
    SellerbotService,
    Campaign,
    SalesProfile,
    Product
} from '../../service/sellerbotService';

// ==================== 📋 INTERFACES ====================

interface CampaignsTabProps {
    language: 'es' | 'en';
    onCampaignsChange?: (campaigns: Campaign[]) => void;
}

interface CampaignFormData {
    name: string;
    description: string;
    agentId: string;
    productId: string;
    targetList: string;
    maxContactsPerHour: number;
    workingHours: {
        start: string;
        end: string;
    };
    workingDays: string[];
    autoAssignContacts: boolean;
    priority: string;
    endDate: string;
}

// ==================== 🎨 ESTILOS CSS ====================
const customStyles = `
  .campaigns-tab .campaign-card {
    transition: all 0.3s ease !important;
    border: 1px solid #e9ecef !important;
    box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075) !important;
  }

  .campaigns-tab .campaign-card:hover {
    box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
    transform: translateY(-2px) !important;
    z-index: 1 !important;
  }

  .campaigns-tab .status-running {
    background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
    color: white;
  }

  .campaigns-tab .status-paused {
    background: linear-gradient(135deg, #ffc107 0%, #fd7e14 100%);
    color: #212529;
  }

  .campaigns-tab .status-completed {
    background: linear-gradient(135deg, #17a2b8 0%, #6f42c1 100%);
    color: white;
  }

  .campaigns-tab .status-scheduled {
    background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
    color: white;
  }

  .campaigns-tab .metric-card {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    border: none;
    transition: all 0.3s ease;
  }

  .campaigns-tab .metric-card:hover {
    background: linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%);
  }

  @media (max-width: 768px) {
    .campaigns-tab .campaign-card:hover {
      transform: none;
    }
  }
`;

// ==================== 🎯 COMPONENTE PRINCIPAL ====================
const CampaignsTab: React.FC<CampaignsTabProps> = ({ language, onCampaignsChange }) => {
    
    // ==================== HOOKS DE DATOS ====================
    const {
        campaigns,
        loading: campaignsLoading,
        error: campaignsError,
        refetch: refetchCampaigns,
        createCampaign,
        toggleCampaign
    } = useSellerbotCampaigns();

    const { profiles } = useSellerbotProfiles();
    const { products } = useSellerbotProducts();

    // ==================== ESTADOS LOCALES ====================
    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [viewMode, setViewMode] = useState<'cards' | 'table' | 'list'>('cards');
    const [selectedItem, setSelectedItem] = useState<Campaign | null>(null);
    const [campaignFormData, setCampaignFormData] = useState<CampaignFormData>({
        name: '',
        description: '',
        agentId: '',
        productId: '',
        targetList: 'general',
        maxContactsPerHour: 10,
        workingHours: { start: '09:00', end: '18:00' },
        workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        autoAssignContacts: true,
        priority: 'medium',
        endDate: ''
    });
    const [isRestarting, setIsRestarting] = useState(false);
    const [formError, setFormError] = useState<string | null>(null);

    // Notificar cambios al componente padre
    useEffect(() => {
        if (onCampaignsChange) {
            onCampaignsChange(campaigns);
        }
    }, [campaigns, onCampaignsChange]);

    // ==================== 📝 FUNCIONES PRINCIPALES ====================

    const mapCampaignToBackend = (campaignData: any) => {
    return {
        name: campaignData.name,
        description: campaignData.description,
        profile_id: campaignData.profileId || campaignData.agentId,
        product_id: campaignData.productId,
        target_list: campaignData.targetList,
        status: campaignData.status || 'scheduled',
        start_date: campaignData.startDate,
        end_date: campaignData.endDate,
        schedule_config: campaignData.scheduleConfig || campaignData.schedule_config || {}
    };
};
    
    const handleCreateCampaign = async (formData: CampaignFormData) => {
    try {
        setIsRestarting(true);
        setFormError(null);

        // ✅ CREAR OBJETO EN FORMATO FRONTEND (camelCase)
        const frontendCampaignData = {
            name: formData.name,
            description: formData.description,
            profileId: formData.agentId,           // ✅ Frontend espera profileId
            productId: formData.productId,         // ✅ Frontend espera productId  
            targetList: formData.targetList,       // ✅ Frontend espera targetList
            status: 'scheduled' as const,
            startDate: new Date().toISOString(),   // ✅ Frontend espera startDate
            endDate: formData.endDate ? 
                new Date(formData.endDate).toISOString() : 
                undefined,                          // ✅ Frontend espera endDate
            scheduleConfig: {                       // ✅ Frontend espera scheduleConfig
                enabled: true,
                startTime: formData.workingHours.start,
                endTime: formData.workingHours.end,
                daysOfWeek: formData.workingDays,
                maxContactsPerHour: formData.maxContactsPerHour,
                delayBetweenMessages: 30
            },
            // Campos requeridos por la interface Campaign
            totalContacts: 0,
            contactsReached: 0,
            leadsGenerated: 0,
            salesClosed: 0,
            revenue: 0,
            schedule: {
                enabled: true,
                startTime: formData.workingHours.start,
                endTime: formData.workingHours.end,
                daysOfWeek: formData.workingDays,
                maxContactsPerHour: formData.maxContactsPerHour,
                delayBetweenMessages: 30
            },
            priority: formData.priority as any,
            maxContactsPerHour: formData.maxContactsPerHour,
            workingHours: formData.workingHours,
            workingDays: formData.workingDays,
            autoAssignContacts: formData.autoAssignContacts
        };

        // ✅ LÓGICA PARA CREAR O EDITAR
        if (selectedItem) {
            // Para editar: convertir a formato backend
            const backendData = mapCampaignToBackend(frontendCampaignData);
            await SellerbotService.updateCampaign(selectedItem.id, backendData);
        } else {
            // Para crear: usar función del hook que maneja la conversión internamente
            await createCampaign(frontendCampaignData);
        }

        await refetchCampaigns();
        setShowCampaignModal(false);
        setSelectedItem(null);
        resetForm();
        
    } catch (error: any) {
        console.error('Error al crear/actualizar campaña:', error);
        setFormError(error.response?.data?.message || error.message || 'Error al procesar la campaña');
    } finally {
        setIsRestarting(false);
    }
};

    const handleEditCampaign = (campaign: Campaign) => {
        setSelectedItem(campaign);
        
        const scheduleConfig = campaign.schedule || {
            enabled: false,
            startTime: '09:00',
            endTime: '18:00',
            daysOfWeek: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            maxContactsPerHour: 10,
            delayBetweenMessages: 30
        };

        setCampaignFormData({
        name: campaign.name,
        description: campaign.description,
        agentId: campaign.profileId || '',     // ✅ Correcto: lee profileId del objeto Campaign
        productId: campaign.productId || '',   // ✅ Correcto: lee productId del objeto Campaign
        targetList: campaign.targetList || 'general',
        maxContactsPerHour: scheduleConfig.maxContactsPerHour || 10,
        workingHours: { 
            start: scheduleConfig.startTime || '09:00', 
            end: scheduleConfig.endTime || '18:00' 
        },
        workingDays: scheduleConfig.daysOfWeek || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        autoAssignContacts: campaign.autoAssignContacts || true,
        priority: campaign.priority || 'medium',
        endDate: campaign.endDate || ''
    });
        
        setShowCampaignModal(true);
    };

    const handleDeleteCampaign = async (campaignId: string) => {
        if (window.confirm(getText('confirmDelete'))) {
            try {
                await SellerbotService.deleteCampaign(campaignId);
                await refetchCampaigns();
            } catch (error: any) {
                console.error('Error eliminando campaña:', error);
            }
        }
    };

    const handleToggleCampaign = async (campaignId: string) => {
        try {
            await toggleCampaign(campaignId);
        } catch (error: any) {
            console.error('Error cambiando estado de campaña:', error);
        }
    };

    const resetForm = () => {
        setCampaignFormData({
            name: '',
            description: '',
            agentId: '',
            productId: '',
            targetList: 'general',
            maxContactsPerHour: 10,
            workingHours: { start: '09:00', end: '18:00' },
            workingDays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            autoAssignContacts: true,
            priority: 'medium',
            endDate: ''
        });
    };

    // ==================== 🎨 FUNCIONES DE UTILIDAD ====================
    
    const getText = (key: string) => {
        const texts: Record<string, Record<string, string>> = {
            es: {
                title: 'Gestión de Campañas',
                createCampaign: 'Crear Campaña',
                loading: 'Cargando...',
                error: 'Error',
                noCampaigns: 'No hay campañas creadas',
                active: 'Activa',
                paused: 'Pausada',
                completed: 'Completada',
                scheduled: 'Programada',
                edit: 'Editar',
                delete: 'Eliminar',
                start: 'Iniciar',
                pause: 'Pausar',
                stop: 'Detener',
                save: 'Guardar',
                cancel: 'Cancelar',
                confirmDelete: '¿Estás seguro de que quieres eliminar esta campaña?',
                name: 'Nombre de la Campaña',
                description: 'Descripción',
                priority: 'Prioridad',
                agent: 'Agente Asignado',
                product: 'Producto',
                targetList: 'Lista Objetivo',
                contactsPerHour: 'Contactos por Hora',
                workingHours: 'Horario de Trabajo',
                workingDays: 'Días Laborales',
                autoAssign: 'Auto-asignar contactos nuevos',
                endDate: 'Fecha de Finalización',
                campaigns: 'Campañas',
                totalContacts: 'Contactos Totales',
                activeCampaigns: 'Campañas Activas',
                conversionRate: 'Tasa de Conversión'
            },
            en: {
                title: 'Campaign Management',
                createCampaign: 'Create Campaign',
                loading: 'Loading...',
                error: 'Error',
                noCampaigns: 'No campaigns created',
                active: 'Active',
                paused: 'Paused',
                completed: 'Completed',
                scheduled: 'Scheduled',
                edit: 'Edit',
                delete: 'Delete',
                start: 'Start',
                pause: 'Pause',
                stop: 'Stop',
                save: 'Save',
                cancel: 'Cancel',
                confirmDelete: 'Are you sure you want to delete this campaign?',
                name: 'Campaign Name',
                description: 'Description',
                priority: 'Priority',
                agent: 'Assigned Agent',
                product: 'Product',
                targetList: 'Target List',
                contactsPerHour: 'Contacts per Hour',
                workingHours: 'Working Hours',
                workingDays: 'Working Days',
                autoAssign: 'Auto-assign new contacts',
                endDate: 'End Date',
                campaigns: 'Campaigns',
                totalContacts: 'Total Contacts',
                activeCampaigns: 'Active Campaigns',
                conversionRate: 'Conversion Rate'
            }
        };
        return texts[language]?.[key] || key;
    };

    const getStatusColor = (status: string) => {
        const colors = {
            running: 'success',
            paused: 'warning', 
            completed: 'info',
            scheduled: 'secondary'
        };
        return colors[status as keyof typeof colors] || 'secondary';
    };

    const getStatusText = (status: string) => {
        const statusTexts = {
            running: getText('active'),
            paused: getText('paused'),
            completed: getText('completed'),
            scheduled: getText('scheduled')
        };
        return statusTexts[status as keyof typeof statusTexts] || status;
    };

    const getAgentName = (campaign: Campaign) => {
        const profile = profiles.find(p => p.id === campaign.profileId);
        return profile ? `${profile.name} (${profile.personality})` : 'Sin agente';
    };

    const getProductName = (campaign: Campaign) => {
        const product = products.find(p => p.id === campaign.productId);
        return product ? product.name : 'Sin producto';
    };

    // ==================== 🎯 RENDERIZADO PRINCIPAL ====================
    
    if (campaignsLoading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" />
                <p className="mt-2">{getText('loading')}</p>
            </div>
        );
    }

    if (campaignsError) {
        return (
            <Alert variant="danger" className="m-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {getText('error')}: {campaignsError}
            </Alert>
        );
    }

    return (
        <div className="campaigns-tab">
            <style>{customStyles}</style>
            
            {/* Header con selector de vista */}
            <Row className="mb-3">
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>
                            <FontAwesomeIcon icon={faBullseye} className="me-2" />
                            {getText('title')} ({campaigns.length})
                        </h4>
                        <div>
                            {/* ✅ NUEVO: Botones de vista con iconos */}
                            <div className="btn-group me-2" role="group">
                                <Button
                                    variant={viewMode === 'cards' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setViewMode('cards')}
                                    title={language === 'es' ? 'Vista Tarjetas' : 'Cards View'}
                                >
                                    <FontAwesomeIcon icon={faBoxOpen} />
                                </Button>
                                <Button
                                    variant={viewMode === 'table' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setViewMode('table')}
                                    title={language === 'es' ? 'Vista Tabla' : 'Table View'}
                                >
                                    <FontAwesomeIcon icon={faUsers} />
                                </Button>
                                <Button
                                    variant={viewMode === 'list' ? 'primary' : 'outline-secondary'}
                                    size="sm"
                                    onClick={() => setViewMode('list')}
                                    title={language === 'es' ? 'Vista Lista' : 'List View'}
                                >
                                    <FontAwesomeIcon icon={faChartLine} />
                                </Button>
                            </div>
                            <Button 
                                variant="primary"
                                onClick={() => {
                                    setSelectedItem(null);
                                    resetForm();
                                    setShowCampaignModal(true);
                                }}
                                className="me-2"
                            >
                                <FontAwesomeIcon icon={faPlus} className="me-1" />
                                {getText('createCampaign')}
                            </Button>
                            <Button 
                                variant="outline-secondary" 
                                onClick={() => refetchCampaigns()} 
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faSync} />
                            </Button>
                        </div>
                    </div>

                    {/* Métricas overview */}
                    <Row className="g-3 mb-4">
                        <Col md={3} sm={6}>
                            <Card className="metric-card text-center">
                                <Card.Body>
                                    <FontAwesomeIcon icon={faBullseye} className="text-primary mb-2" size="2x" />
                                    <h4 className="mb-0">{campaigns.length}</h4>
                                    <small className="text-muted">{getText('campaigns')}</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} sm={6}>
                            <Card className="metric-card text-center">
                                <Card.Body>
                                    <FontAwesomeIcon icon={faPlay} className="text-success mb-2" size="2x" />
                                    <h4 className="mb-0">{campaigns.filter(c => c.status === 'running').length}</h4>
                                    <small className="text-muted">{getText('activeCampaigns')}</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} sm={6}>
                            <Card className="metric-card text-center">
                                <Card.Body>
                                    <FontAwesomeIcon icon={faUsers} className="text-info mb-2" size="2x" />
                                    <h4 className="mb-0">
                                        {campaigns.reduce((total, c) => total + (c.contactsReached || 0), 0)}
                                    </h4>
                                    <small className="text-muted">{getText('totalContacts')}</small>
                                </Card.Body>
                            </Card>
                        </Col>
                        <Col md={3} sm={6}>
                            <Card className="metric-card text-center">
                                <Card.Body>
                                    <FontAwesomeIcon icon={faPercent} className="text-warning mb-2" size="2x" />
                                    <h4 className="mb-0">
                                        {campaigns.length > 0 
                                            ? (campaigns.reduce((total, c) => {
                                                const rate = c.contactsReached > 0 ? (c.salesClosed / c.contactsReached) * 100 : 0;
                                                return total + rate;
                                              }, 0) / campaigns.length).toFixed(1)
                                            : '0.0'
                                        }%
                                    </h4>
                                    <small className="text-muted">{getText('conversionRate')}</small>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* Contenido condicional según vista seleccionada */}
            {campaigns.length === 0 ? (
                <Card>
                    <Card.Body className="text-center py-5">
                        <FontAwesomeIcon icon={faBullseye} size="3x" className="text-muted mb-3" />
                        <h5 className="text-muted">{getText('noCampaigns')}</h5>
                        <p className="text-muted">
                            {language === 'es' 
                                ? 'Crea tu primera campaña para comenzar'
                                : 'Create your first campaign to get started'
                            }
                        </p>
                        <Button 
                            variant="primary"
                            onClick={() => {
                                setSelectedItem(null);
                                resetForm();
                                setShowCampaignModal(true);
                            }}
                        >
                            <FontAwesomeIcon icon={faPlus} className="me-2" />
                            {language === 'es' ? 'Crear Primera Campaña' : 'Create First Campaign'}
                        </Button>
                    </Card.Body>
                </Card>
            ) : (
                <>
                    {/* VISTA CARDS */}
                    {viewMode === 'cards' && (
                        <Row className="g-3">
                            {campaigns.map((campaign) => (
                                <Col key={campaign.id} lg={6} xl={4} className="mb-3">
                                    <Card className={`h-100 campaign-card status-${campaign.status}`}>
                                        <Card.Header className={`d-flex justify-content-between align-items-center status-${campaign.status}`}>
                                            <div>
                                                <h6 className="mb-0">{campaign.name}</h6>
                                                <small className="opacity-75">
                                                    {getAgentName(campaign)}
                                                </small>
                                            </div>
                                            <div className="d-flex align-items-center">
                                                <Badge bg="light" text="dark" className="me-2">
                                                    {getStatusText(campaign.status)}
                                                </Badge>
                                                <Dropdown>
                                                    <Dropdown.Toggle variant="outline-light" size="sm">
                                                        <FontAwesomeIcon icon={faCog} />
                                                    </Dropdown.Toggle>
                                                    <Dropdown.Menu>
                                                        <Dropdown.Item onClick={() => handleEditCampaign(campaign)}>
                                                            <FontAwesomeIcon icon={faEdit} className="me-1" />
                                                            {getText('edit')}
                                                        </Dropdown.Item>
                                                        {campaign.status === 'running' ? (
                                                            <Dropdown.Item onClick={() => handleToggleCampaign(campaign.id)}>
                                                                <FontAwesomeIcon icon={faPause} className="me-1" />
                                                                {getText('pause')}
                                                            </Dropdown.Item>
                                                        ) : (
                                                            <Dropdown.Item onClick={() => handleToggleCampaign(campaign.id)}>
                                                                <FontAwesomeIcon icon={faPlay} className="me-1" />
                                                                {getText('start')}
                                                            </Dropdown.Item>
                                                        )}
                                                        <Dropdown.Divider />
                                                        <Dropdown.Item 
                                                            className="text-danger"
                                                            onClick={() => handleDeleteCampaign(campaign.id)}
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} className="me-1" />
                                                            {getText('delete')}
                                                        </Dropdown.Item>
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </Card.Header>

                                        <Card.Body>
                                            <p className="text-muted small mb-3">
                                                {campaign.description.length > 100 
                                                    ? `${campaign.description.substring(0, 100)}...`
                                                    : campaign.description
                                                }
                                            </p>

                                            <Row className="g-2 mb-3">
                                                <Col xs={6}>
                                                    <div className="text-center">
                                                        <div className="fw-bold text-primary">{campaign.contactsReached || 0}</div>
                                                        <div className="small text-muted">
                                                            {language === 'es' ? 'Contactados' : 'Contacted'}
                                                        </div>
                                                    </div>
                                                </Col>
                                                <Col xs={6}>
                                                    <div className="text-center">
                                                        <div className="fw-bold text-success">{campaign.salesClosed || 0}</div>
                                                        <div className="small text-muted">
                                                            {language === 'es' ? 'Ventas' : 'Sales'}
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>

                                            <div className="mb-2">
                                                <div className="d-flex justify-content-between align-items-center mb-1">
                                                    <small className="text-muted">
                                                        {language === 'es' ? 'Progreso:' : 'Progress:'}
                                                    </small>
                                                    <small className="fw-semibold">
                                                        {((campaign.salesClosed || 0) / Math.max(campaign.contactsReached || 1, 1) * 100).toFixed(1)}%
                                                    </small>
                                                </div>
                                                <ProgressBar 
                                                    now={(campaign.salesClosed || 0) / Math.max(campaign.contactsReached || 1, 1) * 100}
                                                    variant={
                                                        campaign.status === 'running' ? 'success' :
                                                        campaign.status === 'paused' ? 'warning' : 'info'
                                                    }
                                                />
                                            </div>

                                            <div className="small text-muted">
                                                <div><strong>{language === 'es' ? 'Producto:' : 'Product:'}</strong> {getProductName(campaign)}</div>
                                                <div><strong>{language === 'es' ? 'Creada:' : 'Created:'}</strong> {campaign.createdAt ? new Date(campaign.createdAt).toLocaleDateString() : 'N/A'}</div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    )}

                    {/* VISTA TABLA */}
                    {viewMode === 'table' && (
                        <Card>
                            <Card.Body>
                                <Table responsive hover>
                                    <thead>
                                        <tr>
                                            <th>{language === 'es' ? 'Campaña' : 'Campaign'}</th>
                                            <th>{language === 'es' ? 'Estado' : 'Status'}</th>
                                            <th>{language === 'es' ? 'Agente' : 'Agent'}</th>
                                            <th>{language === 'es' ? 'Contactos' : 'Contacts'}</th>
                                            <th>{language === 'es' ? 'Ventas' : 'Sales'}</th>
                                            <th>{language === 'es' ? 'Progreso' : 'Progress'}</th>
                                            <th>{language === 'es' ? 'Acciones' : 'Actions'}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {campaigns.map((campaign) => (
                                            <tr key={campaign.id}>
                                                <td>
                                                    <div>
                                                        <strong>{campaign.name}</strong>
                                                        <br />
                                                        <small className="text-muted">
                                                            {getProductName(campaign)}
                                                        </small>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge bg={getStatusColor(campaign.status)}>
                                                        {getStatusText(campaign.status)}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <small>{getAgentName(campaign)}</small>
                                                </td>
                                                <td>
                                                    <span className="fw-bold">{campaign.contactsReached || 0}</span>
                                                </td>
                                                <td>
                                                    <span className="fw-bold text-success">{campaign.salesClosed || 0}</span>
                                                </td>
                                                <td style={{ minWidth: '120px' }}>
                                                    <ProgressBar 
                                                        now={(campaign.salesClosed || 0) / Math.max(campaign.contactsReached || 1, 1) * 100}
                                                        variant={getStatusColor(campaign.status)}
                                                        label={`${((campaign.salesClosed || 0) / Math.max(campaign.contactsReached || 1, 1) * 100).toFixed(1)}%`}
                                                    />
                                                </td>
                                                <td>
                                                    <div className="d-flex gap-1">
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                            onClick={() => handleEditCampaign(campaign)}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant={campaign.status === 'running' ? 'outline-warning' : 'outline-success'}
                                            onClick={() => handleToggleCampaign(campaign.id)}
                                        >
                                            <FontAwesomeIcon icon={campaign.status === 'running' ? faPause : faPlay} />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => handleDeleteCampaign(campaign.id)}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    )}

    {/* VISTA LISTA */}
    {viewMode === 'list' && (
        <Card>
            <Card.Body>
                {campaigns.map((campaign, index) => (
                    <div key={campaign.id} className={`d-flex justify-content-between align-items-center py-3 ${index < campaigns.length - 1 ? 'border-bottom' : ''}`}>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                                <Badge bg={getStatusColor(campaign.status)} className="me-2">
                                    {getStatusText(campaign.status)}
                                </Badge>
                                <h6 className="mb-0 me-2">{campaign.name}</h6>
                                <small className="text-muted">• {getAgentName(campaign)}</small>
                            </div>
                            <div className="d-flex align-items-center text-muted small">
                                <span className="me-3">
                                    <FontAwesomeIcon icon={faUsers} className="me-1" />
                                    {campaign.contactsReached || 0} contactados
                                </span>
                                <span className="me-3">
                                    <FontAwesomeIcon icon={faHandshake} className="me-1" />
                                    {campaign.salesClosed || 0} ventas
                                </span>
                                <span>
                                    <FontAwesomeIcon icon={faDollarSign} className="me-1" />
                                    ${(campaign.revenue || 0).toLocaleString()}
                                </span>
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <div className="text-center me-3">
                                <div className="fw-bold text-primary">
                                    {((campaign.salesClosed || 0) / Math.max(campaign.contactsReached || 1, 1) * 100).toFixed(1)}%
                                </div>
                                <small className="text-muted">conversión</small>
                            </div>
                            <Dropdown>
                                <Dropdown.Toggle variant="outline-secondary" size="sm">
                                    <FontAwesomeIcon icon={faCog} />
                                </Dropdown.Toggle>
                                <Dropdown.Menu>
                                    <Dropdown.Item onClick={() => handleEditCampaign(campaign)}>
                                        <FontAwesomeIcon icon={faEdit} className="me-1" />
                                        {getText('edit')}
                                    </Dropdown.Item>
                                    <Dropdown.Item onClick={() => handleToggleCampaign(campaign.id)}>
                                        <FontAwesomeIcon icon={campaign.status === 'running' ? faPause : faPlay} className="me-1" />
                                        {campaign.status === 'running' ? getText('pause') : getText('start')}
                                    </Dropdown.Item>
                                    <Dropdown.Divider />
                                    <Dropdown.Item 
                                        className="text-danger"
                                        onClick={() => handleDeleteCampaign(campaign.id)}
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="me-1" />
                                        {getText('delete')}
                                    </Dropdown.Item>
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>
                    </div>
                ))}
            </Card.Body>
        </Card>
    )}
</>
)}

{/* ==================== 🎯 MODAL DE CREACIÓN/EDICIÓN ==================== */}
<Modal 
    show={showCampaignModal} 
    onHide={() => setShowCampaignModal(false)} 
    size="lg"
    centered
>
    <Modal.Header closeButton>
        <Modal.Title>
            <FontAwesomeIcon icon={faBullseye} className="me-2" />
            {selectedItem ? 
                (language === 'es' ? 'Editar Campaña' : 'Edit Campaign') :
                (language === 'es' ? 'Crear Nueva Campaña' : 'Create New Campaign')
            }
        </Modal.Title>
    </Modal.Header>
    <Modal.Body>
        {formError && (
            <Alert variant="danger" className="mb-3">
                <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
                {formError}
            </Alert>
        )}

        <Form>
            <Row>
                <Col md={8}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <FontAwesomeIcon icon={faBullseye} className="me-2" />
                            {getText('name')}
                        </Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={language === 'es' ? 'Ej: Campaña Q4 2024' : 'Ex: Q4 2024 Campaign'}
                            value={campaignFormData.name}
                            onChange={(e) => setCampaignFormData({...campaignFormData, name: e.target.value})}
                            required
                        />
                    </Form.Group>
                </Col>
                <Col md={4}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <FontAwesomeIcon icon={faAward} className="me-2" />
                            {getText('priority')}
                        </Form.Label>
                        <Form.Select
                            value={campaignFormData.priority}
                            onChange={(e) => setCampaignFormData({...campaignFormData, priority: e.target.value})}
                        >
                            <option value="low">{language === 'es' ? 'Baja' : 'Low'}</option>
                            <option value="medium">{language === 'es' ? 'Media' : 'Medium'}</option>
                            <option value="high">{language === 'es' ? 'Alta' : 'High'}</option>
                            <option value="urgent">{language === 'es' ? 'Urgente' : 'Urgent'}</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-3">
                <Form.Label>
                    <FontAwesomeIcon icon={faFileAlt} className="me-2" />
                    {getText('description')}
                </Form.Label>
                <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder={language === 'es' ? 'Describe los objetivos y estrategia de la campaña...' : 'Describe campaign objectives and strategy...'}
                    value={campaignFormData.description}
                    onChange={(e) => setCampaignFormData({...campaignFormData, description: e.target.value})}
                    required
                />
            </Form.Group>

            <Row>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <FontAwesomeIcon icon={faUserTie} className="me-2" />
                            {getText('agent')}
                        </Form.Label>
                        <Form.Select
                            value={campaignFormData.agentId}
                            onChange={(e) => setCampaignFormData({...campaignFormData, agentId: e.target.value})}
                        >
                            <option value="">{language === 'es' ? 'Seleccionar agente...' : 'Select agent...'}</option>
                            {profiles.filter(p => p.isActive).map(profile => (
                                <option key={profile.id} value={profile.id}>
                                    {profile.name} ({profile.personality})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col md={6}>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                            {getText('product')}
                        </Form.Label>
                        <Form.Select
                            value={campaignFormData.productId}
                            onChange={(e) => setCampaignFormData({...campaignFormData, productId: e.target.value})}
                        >
                            <option value="">{language === 'es' ? 'Seleccionar producto...' : 'Select product...'}</option>
                            {products.filter(p => p.isActive).map(product => (
                                <option key={product.id} value={product.id}>
                                    {product.name} (${product.price})
                                </option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Configuración de Ejecución */}
            <Card className="mb-3">
                <Card.Header>
                    <small>
                        <FontAwesomeIcon icon={faCog} className="me-2" />
                        {language === 'es' ? 'Configuración de Ejecución' : 'Execution Settings'}
                    </small>
                </Card.Header>
                <Card.Body>
                    <Row>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small">
                                    {getText('contactsPerHour')}
                                </Form.Label>
                                <Form.Range
                                    min={1}
                                    max={50}
                                    value={campaignFormData.maxContactsPerHour}
                                    onChange={(e) => setCampaignFormData({...campaignFormData, maxContactsPerHour: parseInt(e.target.value)})}
                                />
                                <div className="text-center small text-muted">
                                    {campaignFormData.maxContactsPerHour} contactos/hora
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small">
                                    {language === 'es' ? 'Hora Inicio' : 'Start Time'}
                                </Form.Label>
                                <Form.Control
                                    type="time"
                                    value={campaignFormData.workingHours.start}
                                    onChange={(e) => setCampaignFormData({
                                        ...campaignFormData, 
                                        workingHours: {...campaignFormData.workingHours, start: e.target.value}
                                    })}
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small">
                                    {language === 'es' ? 'Hora Fin' : 'End Time'}
                                </Form.Label>
                                <Form.Control
                                    type="time"
                                    value={campaignFormData.workingHours.end}
                                    onChange={(e) => setCampaignFormData({
                                        ...campaignFormData, 
                                        workingHours: {...campaignFormData.workingHours, end: e.target.value}
                                    })}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Días de trabajo */}
                    <Form.Group className="mb-3">
                        <Form.Label className="small">{getText('workingDays')}</Form.Label>
                        <div className="d-flex flex-wrap gap-2">
                            {[
                                { key: 'monday', label: language === 'es' ? 'Lun' : 'Mon' },
                                { key: 'tuesday', label: language === 'es' ? 'Mar' : 'Tue' },
                                { key: 'wednesday', label: language === 'es' ? 'Mié' : 'Wed' },
                                { key: 'thursday', label: language === 'es' ? 'Jue' : 'Thu' },
                                { key: 'friday', label: language === 'es' ? 'Vie' : 'Fri' },
                                { key: 'saturday', label: language === 'es' ? 'Sáb' : 'Sat' },
                                { key: 'sunday', label: language === 'es' ? 'Dom' : 'Sun' }
                            ].map(day => (
                                <Form.Check
                                    key={day.key}
                                    type="checkbox"
                                    id={`day-${day.key}`}
                                    label={day.label}
                                    checked={campaignFormData.workingDays.includes(day.key)}
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setCampaignFormData({
                                                ...campaignFormData,
                                                workingDays: [...campaignFormData.workingDays, day.key]
                                            });
                                        } else {
                                            setCampaignFormData({
                                                ...campaignFormData,
                                                workingDays: campaignFormData.workingDays.filter(d => d !== day.key)
                                            });
                                        }
                                    }}
                                />
                            ))}
                        </div>
                    </Form.Group>

                    {/* Opciones adicionales */}
                    <Row>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small">
                                    {language === 'es' ? 'Lista Objetivo' : 'Target List'}
                                </Form.Label>
                                <Form.Select
                                    value={campaignFormData.targetList}
                                    onChange={(e) => setCampaignFormData({...campaignFormData, targetList: e.target.value})}
                                >
                                    <option value="general">{language === 'es' ? 'General' : 'General'}</option>
                                    <option value="premium">{language === 'es' ? 'Premium' : 'Premium'}</option>
                                    <option value="leads">{language === 'es' ? 'Leads Calificados' : 'Qualified Leads'}</option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group className="mb-3">
                                <Form.Label className="small">
                                    {getText('endDate')} ({language === 'es' ? 'Opcional' : 'Optional'})
                                </Form.Label>
                                <Form.Control
                                    type="date"
                                    value={campaignFormData.endDate}
                                    onChange={(e) => setCampaignFormData({...campaignFormData, endDate: e.target.value})}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* Auto-asignación */}
                    <Form.Check
                        type="switch"
                        id="auto-assign-switch"
                        label={getText('autoAssign')}
                        checked={campaignFormData.autoAssignContacts}
                        onChange={(e) => setCampaignFormData({...campaignFormData, autoAssignContacts: e.target.checked})}
                    />
                </Card.Body>
            </Card>
        </Form>
    </Modal.Body>
    <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowCampaignModal(false)}>
            {getText('cancel')}
        </Button>
        <Button 
            variant="primary" 
            onClick={() => handleCreateCampaign(campaignFormData)}
            disabled={!campaignFormData.name || !campaignFormData.description || isRestarting}
        >
            {isRestarting ? (
                <>
                    <Spinner size="sm" className="me-2" />
                    {language === 'es' ? 'Guardando...' : 'Saving...'}
                </>
            ) : (
                <>
                    <FontAwesomeIcon icon={selectedItem ? faEdit : faPlus} className="me-2" />
                    {selectedItem ? 
                        (language === 'es' ? 'Actualizar' : 'Update') :
                        (language === 'es' ? 'Crear Campaña' : 'Create Campaign')
                    }
                </>
            )}
        </Button>
    </Modal.Footer>
</Modal>
</div>
);
};

export default CampaignsTab;

/*
==================== 📋 DOCUMENTACIÓN PARA IMPLEMENTACIÓN ====================

🎯 ARCHIVO COMPLETO:
- CampaignsTab.tsx (2 partes combinadas)

🔗 INTEGRACIÓN EN AISeller.tsx:
1. Importar: import CampaignsTab from './CampaignsTab';
2. Añadir pestaña en el Nav:
   <Nav.Item>
     <Nav.Link eventKey="campaigns">
       <FontAwesomeIcon icon={faBullseye} className="me-1" />
       {language === 'es' ? 'Campañas' : 'Campaigns'}
     </Nav.Link>
   </Nav.Item>

3. Añadir Tab.Pane:
   <Tab.Pane eventKey="campaigns">
     <CampaignsTab 
       language={language}
       onCampaignsChange={(campaigns) => console.log('Campañas actualizadas:', campaigns)}
     />
   </Tab.Pane>

🗑️ ELIMINAR DE AISeller.tsx:
- Todo el estado relacionado a campañas
- Todas las funciones de campañas  
- Todos los modales de campañas
- La pestaña "campaigns" del Tab.Container actual

🚀 CARACTERÍSTICAS COMPLETADAS:

✅ SELECTOR DE VISTA:
- 📊 Vista Cards (por defecto) - Tarjetas visuales con métricas
- 📋 Vista Tabla - Tabla compacta para gestión rápida  
- 📈 Vista Lista - Lista simple con métricas inline

✅ FUNCIONALIDAD COMPLETA:
- Crear, editar, eliminar campañas
- Iniciar, pausar, detener campañas
- Modal de configuración avanzada
- Métricas overview en tiempo real
- Integración con endpoints existentes

✅ MEJORAS VISUALES:
- Cards coloridas por estado
- Hover effects y animaciones
- Progress bars por campaña
- Estados visuales dinámicos

✅ SIN ERRORES TYPESCRIPT:
- Todos los campos mapeados correctamente
- Interfaces alineadas con Campaign
- Campos opcionales validados
- Cálculos de conversión corregidos

El componente está 100% funcional y listo para integrar.
Elimina el modal redundante y usa el selector de vista limpio.
*/